import type { Ref } from "vue";
import { useCartStore } from "~/stores/cart";
import { useToast } from "~/composables/useToast";
import { readVariantQty } from "~/composables/product/variantDisplay";
import { flyToCart } from "~/utils/flyToCart";

/* ====================================================================
 *  useCardAddToCart — додавання в кошик З КАРТКИ товару:
 *  фіксований variant-проп, qty завжди 1, пікер опцій для товарів без
 *  преселекту. Гард стоку: не даємо додати більше, ніж лишилось
 *  (з урахуванням уже доданого в кошик).
 *
 *  НЕ плутати з useProductCart (сторінка товару): там реактивний вибір
 *  варіанта і qty-степер — інша модель, спільне винесено у
 *  variantDisplay/buildGaItem.
 * ==================================================================== */

type AnyRecord = Record<string, unknown>;

interface CardAddToCartCtx {
  product: () => AnyRecord;
  variant: () => AnyRecord | null | undefined;
  selectedColor: Ref<string>;
  selectedSize: Ref<string>;
  needColor: Ref<boolean>;
  needSize: Ref<boolean>;
  canSubmit: Ref<boolean>;
  priceToShow: Ref<number>;
  isOutOfStock: Ref<boolean>;
  coverUrl: Ref<string>;
  imgRef: Ref<HTMLImageElement | null>;
  showPicker: Ref<boolean>;
  /** Трекінг після успішного cart.add (GA + Pixel живуть у викликача) */
  onAdded: () => void;
}

export function useCardAddToCart({
  product,
  variant,
  selectedColor,
  selectedSize,
  needColor,
  needSize,
  canSubmit,
  priceToShow,
  isOutOfStock,
  coverUrl,
  imgRef,
  showPicker,
  onAdded,
}: CardAddToCartCtx) {
  const cart = useCartStore();
  const { show: showToast } = useToast();

  function getQtyInCart(productId: unknown, variantId: string): number {
    const list = cart.items || [];
    if (!Array.isArray(list)) return 0;

    const pid = String(productId || "");

    return list
      .filter((it) => String(it?.productId || "") === pid)
      .filter((it) => String(it?.variantId || "") === variantId)
      .reduce((sum, it) => {
        const q = Number(it?.qty ?? 0);
        return sum + (isFinite(q) ? q : 0);
      }, 0);
  }

  function onAddClick() {
    if (isOutOfStock.value) return;
    const v = variant();
    const hasPreselected =
      (!!((v?.color as AnyRecord)?.value ?? v?.color) || !needColor.value) &&
      (!!v?.size || !needSize.value);
    if (hasPreselected) {
      confirmAddToCart(true);
      return;
    }
    showPicker.value = !showPicker.value;
  }

  function confirmAddToCart(preselected = false) {
    if (!preselected && !canSubmit.value) {
      showPicker.value = true;
      return;
    }

    const p = product();
    const v = variant();

    // Жорсткий гард: не додаємо більше, ніж доступно для обраного варіанта.
    const vid = String(v?.id || "");
    const availableQty = readVariantQty(v); // ТІЛЬКИ по варіанту, без product-фолбеку
    const inCartQty = vid ? getQtyInCart(p.id, vid) : 0;
    const nextQty = inCartQty + 1;

    if (
      typeof availableQty === "number" &&
      isFinite(availableQty) &&
      availableQty >= 0
    ) {
      if (availableQty === 0) {
        showToast("Цього варіанту вже немає в наявності", { type: "error" });
        showPicker.value = false;
        return;
      }
      if (nextQty > availableQty) {
        showToast(
          `В наявності лише ${availableQty} шт. Цей лот уже в кошику (${inCartQty} шт.)`,
          {
            type: "error",
          },
        );
        showPicker.value = false;
        return;
      }
    }

    const c = preselected
      ? ((v?.color as AnyRecord)?.value ?? v?.color ?? "")
      : selectedColor.value || "";
    const s = preselected ? (v?.size ?? "") : selectedSize.value || "";

    cart.add({
      productId: p.id,
      variantId: String(v?.id || ""),
      sku: String(v?.sku || ""),
      slug: p.slug as string | undefined,
      title: p.title,
      price: Number(priceToShow.value || 0),
      image:
        coverUrl.value ||
        (Array.isArray(p.images) && p.images[0] ? p.images[0] : ""),
      color: String(c || ""),
      size: String(s || ""),
      qty: 1,
    });

    onAdded();

    showPicker.value = false;
    flyToCart(imgRef.value, coverUrl.value); // ✨ анімація
  }

  return { onAddClick, confirmAddToCart };
}
